import React, { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Button, 
  Container, 
  Box, 
  TextField, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem,
  Grid,
  Paper,
  Typography
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { BreadcrumbContext } from "../../../components/Breadcrumb";
import {
  FirebaseAuth,
  Firestore,
} from "../../../components/FirebaseAuth/firebase";
import { confirmAlert } from "react-confirm-alert";
import { toast } from "react-toastify";
import { ServiceEditCard } from "../../../components/Service/ServiceEditCard";
import {
  LOCAL_STORAGE_CURRENT_ACCOUNT,
  LOCAL_STORAGE_CURRENT_ORG,
  getLocalStorage,
} from "../../../utils/localStorage";
import { serviceFilterTypes } from "../../../utils/constants";
import { collection, getDocs, query, orderBy, doc, deleteDoc } from "firebase/firestore";
const useStyles = makeStyles((theme) => ({
  container: {
    width: "100%",
  },
  headerContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  btnContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "right",
    padding: theme.spacing(1.5),
    [theme.breakpoints.down("md")]: {
      padding: 0,
    },
  },
  filterContainer: {
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    border: "1px solid rgba(0, 0, 0, 0.12)",
  },
  filterRow: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "center",
    [theme.breakpoints.down("md")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
}));

const ServiceLists = () => {
  const history = useNavigate();
  const classes = useStyles();
  const currentOrganization = getLocalStorage(LOCAL_STORAGE_CURRENT_ORG);
  const currentAccount = getLocalStorage(LOCAL_STORAGE_CURRENT_ACCOUNT);
  const { setBreadcrumb } = useContext(BreadcrumbContext);
  const [services, setServices] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useState("allServices");
  const [keywordFilter, setKeywordFilter] = useState("");

  const handleAdd = useCallback(() => {
    history("/serviceLists/create");
  }, [history]);

  const filterServices = useCallback(() => {
    let filtered = services;

    // Filter by type
    if (typeFilter !== "allServices") {
      // Map the filter values back to actual service types
      const typeMapping = {
        "creativeServices": "Creative Services",
        "developmentServices": "Development Services", 
        "projectManagement": "Project Management",
        "extension": "extension"
      };
      
      const targetType = typeMapping[typeFilter] || typeFilter;
      filtered = filtered.filter(service => service.type === targetType);
    }

    // Filter by keyword
    if (keywordFilter.trim()) {
      const keyword = keywordFilter.toLowerCase();
      filtered = filtered.filter(service => 
        service.name?.toLowerCase().includes(keyword) ||
        service.description?.toLowerCase().includes(keyword) ||
        service.provider?.toLowerCase().includes(keyword)
      );
    }

    setFilteredServices(filtered);
  }, [services, typeFilter, keywordFilter]);

  // const getServiceLists = useCallback(() => {
  //   const serviceQuery = FirebaseAuth.firestore()
  //     .collection("services")
  //     .orderBy("type", "asc");
  //   let temp = [];
  //   setIsLoading(true);
  //   serviceQuery.get().then((serviceSnapShots) => {
  //     serviceSnapShots.forEach((doc) => {
  //       if (!currentAccount?.id || currentOrganization?.id) {
  //         temp.push({ id: doc.id, ...doc.data() });
  //       } else if (!doc.data().showAccounts && !doc.data().showOrganizations) {
  //         temp.push({ id: doc.id, ...doc.data() });
  //       } else if (
  //         ((doc.data().showAccounts.length === 0 &&
  //           doc.data().showOrganizations.length === 0) ||
  //           doc.data().showAccounts.includes(currentAccount.id) ||
  //           doc.data().showOrganizations.includes(currentOrganization.id)) &&
  //         !doc.data().excludeCustomerID.includes(currentAccount.id) &&
  //         !doc.data().excludeOrganizationID.includes(currentOrganization.id)
  //       ) {
  //         temp.push({ ...doc.data(), id: doc.id });
  //       }
  //     });
  //     setServices(temp);
  //     setIsLoading(false);
  //   });
  // }, [currentAccount?.id, currentOrganization?.id]);
  const getServiceLists = useCallback(async () => {
    const servicesRef = collection(Firestore, "services");
    const serviceQuery = query(servicesRef, orderBy("type", "asc"));
    let temp = [];
    setIsLoading(true);
    try {
      const serviceSnapShots = await getDocs(serviceQuery);
      serviceSnapShots.forEach((doc) => {
        if (!currentAccount?.id || currentOrganization?.id) {
          temp.push({ id: doc.id, ...doc.data() });
        } else if (!doc.data().showAccounts && !doc.data().showOrganizations) {
          temp.push({ id: doc.id, ...doc.data() });
        } else if (
          ((doc.data().showAccounts.length === 0 &&
            doc.data().showOrganizations.length === 0) ||
            doc.data().showAccounts.includes(currentAccount.id) ||
            doc.data().showOrganizations.includes(currentOrganization.id)) &&
          !doc.data().excludeCustomerID.includes(currentAccount.id) &&
          !doc.data().excludeOrganizationID.includes(currentOrganization.id)
        ) {
          temp.push({ ...doc.data(), id: doc.id });
        }
      });
      setServices(temp);
      // Initial filtered services will be set by useEffect
    } catch (ex) {
      console.log('Error fetching services:', ex);
    } finally {
      setIsLoading(false);
    }
  }, [currentAccount?.id, currentOrganization?.id]);  

  // const handleDeleteService = useCallback(
  //   async (id) => {
  //     try {
  //       await Firestore.collection("services").doc(id).delete();
  //       getServiceLists();
  //       toast.success("Deleted Successfully");
  //     } catch (ex) {
  //       toast.error("Something went wrong");
  //     }
  //   },
  //   [getServiceLists]
  // );
  const handleDeleteService = useCallback(
    async (id) => {
      try {
        const serviceRef = doc(Firestore, "services", id);
        await deleteDoc(serviceRef);
        getServiceLists();
        toast.success("Deleted Successfully");
      } catch (ex) {
        toast.error("Something went wrong");
      }
    },
    [getServiceLists]
  );

  const handleDelete = useCallback(
    (id) => {
      confirmAlert({
        title: "Are you sure?",
        message: "Are you sure you want to delete this? This cannot be undone.",
        buttons: [
          {
            label: "Yes",
            onClick: async () => handleDeleteService(id),
          },
          {
            label: "No",
          },
        ],
      });
    },
    [handleDeleteService]
  );

  useEffect(() => {
    setBreadcrumb([
      {
        to: "/",
        text: "Home",
        active: false,
      },
      {
        to: null,
        text: "Services",
        active: true,
      },
    ]);
  }, [setBreadcrumb]);

  useEffect(() => {
    getServiceLists();
  }, [getServiceLists]);

  useEffect(() => {
    filterServices();
  }, [filterServices]);

  return (
    <Container className={classes.container}>
      <Box className={classes.headerContainer}>
        {/* Create New Service Button */}
        <Box className={classes.btnContainer}>
          <Button
            type="button"
            variant="contained"
            color="primary"
            onClick={handleAdd}
          >
            <Box>Create New Service</Box>
          </Button>
        </Box>

        {/* Filter Section */}
        <Paper className={classes.filterContainer}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Filter Services
          </Typography>
          <Box className={classes.filterRow}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Service Type</InputLabel>
              <Select
                value={typeFilter}
                label="Service Type"
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                {serviceFilterTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <TextField
              size="small"
              label="Search by keyword"
              placeholder="Search name, description, or provider..."
              value={keywordFilter}
              onChange={(e) => setKeywordFilter(e.target.value)}
              sx={{ minWidth: 300, flexGrow: 1 }}
            />

            <Typography variant="body2" color="text.secondary" sx={{ minWidth: "fit-content" }}>
              {filteredServices.length} of {services.length} services
            </Typography>
          </Box>
        </Paper>
      </Box>

      {/* Services List */}
      {!isLoading &&
        filteredServices.length > 0 &&
        filteredServices.map((service) => {
          return (
            <ServiceEditCard
              service={service}
              key={service.id}
              handleDelete={() => handleDelete(service.id)}
            />
          );
        })}
      
      {!isLoading && filteredServices.length === 0 && services.length > 0 && (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="h6" color="text.secondary">
            No services match your current filters
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your search criteria
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default ServiceLists;
